import React from 'react';
import { List, Datagrid, TextField, BooleanField, EmailField, FunctionField, TextInput, SelectInput } from 'react-admin';

/**
 * Employee-type choices for the type filter.
 * Maps the `employee_type` column values to human-readable labels.
 */
const employeeTypeChoices = [
    { id: 'internal', name: 'Internal' },
    { id: 'external', name: 'External' },
];

/**
 * Active-status choices for the terminated filter.
 *
 * The backend defaults to `is_active = 1` when no explicit `is_active` filter
 * is sent. Selecting "Terminated" sends `is_active = 0`, overriding that
 * default so that only terminated users are returned.
 */
const activeStatusChoices = [
    { id: 1, name: 'Active' },
    { id: 0, name: 'Terminated' },
];

/**
 * Known department choices sourced from Azure AD sync.
 * Department values are stored lowercase; labels are title-cased for display.
 * An empty-string option allows the user to clear the filter.
 */
const departmentChoices = [
    { id: 'hr', name: 'HR' },
    { id: 'nexus', name: 'Nexus' },
    { id: 'song', name: 'Song' },
    { id: 'dlvry', name: 'Dlvry' },
    { id: 'anesis', name: 'Anesis' },
];

/**
 * Filter inputs rendered above the user list.
 *
 * Always-on filters:
 * - **Name** (`full_name`) — free-text LIKE search
 * - **Email** (`email`) — free-text LIKE search
 * - **Type** (`employee_type`) — select from known employee types
 * - **Department** (`department`) — select from known Azure AD departments
 * - **Status** (`is_active`) — select Active or Terminated; defaults to Active
 *   (the backend applies `is_active = 1` when no explicit filter is sent)
 */
const userFilters = [
    <TextInput key="full_name" source="full_name" label="Name" alwaysOn />,
    <TextInput key="email" source="email" label="Email" alwaysOn />,
    <SelectInput key="employee_type" source="employee_type" label="Type" choices={employeeTypeChoices} alwaysOn />,
    <SelectInput key="department" source="department" label="Department" choices={departmentChoices} alwaysOn />,
    <SelectInput key="is_active" source="is_active" label="Status" choices={activeStatusChoices} alwaysOn />,
];

/**
 * Displays the list of users (Azure AD identity data) with support for
 * filtering by name, email, employee type, department, and active status.
 *
 * Columns shown:
 * - Full Name
 * - Email
 * - Department (sourced from Azure AD)
 * - Role
 * - Type
 * - Work Schedule
 * - Active status
 *
 * Full Name is the primary/first column — it's what HR admins recognize
 * employees by, unlike the internal `employee_id`, which is intentionally
 * not shown here (mirrors `EmployeeList`, which also leads with Full Name
 * and omits the ID column). The `id` (internal row ID) is likewise hidden.
 *
 * Import Accounting and Export Timesheet actions have been moved to the
 * `/employees` page.
 */
export const UserList: React.FC = () => {
    return (
        <List filters={userFilters}>
            <Datagrid bulkActionButtons={false}>
                <TextField source="full_name" label="Full Name" />
                <EmailField source="email" label="Email" />
                <TextField source="department" label="Department" />
                <TextField source="role" label="Role" />
                <TextField source="employee_type" label="Type" />
                <FunctionField label="Work Schedule" render={(record: any) => record.work_schedule_name || 'Standard (Mon-Fri)'} />
                <BooleanField source="is_active" label="Active" />
            </Datagrid>
        </List>
    );
};
